import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, Search } from 'lucide-react'
import { Button, LoadingSpinner } from '@/components/ui'
import { Modal } from '@/components/ui/modal'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/useToast'
import { contractorsApi } from '@/lib/api'
import type { Contractor, CreateContractorDto } from '@/lib/api'

/* ─── Contractor Modal ─── */

function ContractorModal({
  isOpen,
  onClose,
  editItem,
}: {
  isOpen: boolean
  onClose: () => void
  editItem: Contractor | null
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const isEdit = !!editItem

  const [name, setName] = useState('')
  const [unp, setUnp] = useState('')
  const [address, setAddress] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')

  // Reset form on open
  useEffect(() => {
    if (isOpen && editItem) {
      setName(editItem.name)
      setUnp(editItem.unp)
      setAddress(editItem.address || '')
      setPhone(editItem.phone || '')
      setEmail(editItem.email || '')
      setNote(editItem.note || '')
    } else if (isOpen) {
      setName('')
      setUnp('')
      setAddress('')
      setPhone('')
      setEmail('')
      setNote('')
    }
  }, [isOpen, editItem])

  const createMutation = useMutation({
    mutationFn: (data: CreateContractorDto) => contractorsApi.create(data),
    onSuccess: () => {
      toast.success('Контрагент создан')
      queryClient.invalidateQueries({ queryKey: ['contractors'] })
      onClose()
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Ошибка создания'),
  })

  const updateMutation = useMutation({
    mutationFn: (data: CreateContractorDto) => contractorsApi.update(editItem!.id, data),
    onSuccess: () => {
      toast.success('Контрагент обновлён')
      queryClient.invalidateQueries({ queryKey: ['contractors'] })
      onClose()
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Ошибка обновления'),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return toast.error('Название обязательно')
    if (!unp.trim()) return toast.error('УНП/ИНН обязателен')
    if (!/^\d{9,12}$/.test(unp.trim())) return toast.error('УНП/ИНН должен содержать 9–12 цифр')

    const payload: CreateContractorDto = {
      name: name.trim(),
      unp: unp.trim(),
      address: address.trim(),
      phone: phone.trim(),
      email: email.trim(),
      note: note.trim(),
    }

    if (isEdit) {
      updateMutation.mutate(payload)
    } else {
      createMutation.mutate(payload)
    }
  }

  const isLoading = createMutation.isPending || updateMutation.isPending

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header onClose={onClose}>
        {isEdit ? 'Редактировать контрагента' : 'Добавить контрагента'}
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Наименование <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="ООО «Компания»"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                УНП / ИНН <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="123456789"
                value={unp}
                onChange={e => setUnp(e.target.value.replace(/\D/g, '').slice(0, 12))}
                disabled={isEdit}
              />
              <p className="text-xs text-gray-400 mt-1">9 цифр (Беларусь) или 10–12 (Россия)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Адрес</label>
              <Input placeholder="г. Минск, ул. ..." value={address} onChange={e => setAddress(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Телефон</label>
                <Input placeholder="+375..." value={phone} onChange={e => setPhone(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <Input placeholder="info@company.by" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Примечание</label>
              <Input placeholder="Примечание" value={note} onChange={e => setNote(e.target.value)} />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={onClose}>Отмена</Button>
          <Button type="submit" isLoading={isLoading}>
            {isEdit ? 'Обновить' : 'Создать'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

/* ─── Main Page ─── */

export function ContractorsPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editItem, setEditItem] = useState<Contractor | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['contractors', search],
    queryFn: async () => {
      const res = await contractorsApi.getAll(search || undefined)
      const d = res.data as any
      return (d.contractors ?? []) as Contractor[]
    },
  })

  const contractors = data ?? []

  const deleteMutation = useMutation({
    mutationFn: (id: string) => contractorsApi.delete(id),
    onSuccess: () => {
      toast.success('Контрагент удалён')
      queryClient.invalidateQueries({ queryKey: ['contractors'] })
    },
    onError: () => toast.error('Ошибка удаления'),
  })

  const handleEdit = (item: Contractor) => {
    setEditItem(item)
    setModalOpen(true)
  }

  const handleDelete = (id: string) => {
    if (window.confirm('Удалить контрагента?')) {
      deleteMutation.mutate(id)
    }
  }

  if (isLoading) return <LoadingSpinner />

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Контрагенты</h1>
        <div className="flex items-center gap-2">
          <Button onClick={() => { setEditItem(null); setModalOpen(true) }}>
            <Plus className="h-4 w-4" /> Добавить
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Поиск по имени или УНП..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 font-medium text-gray-600">Наименование</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">УНП / ИНН</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Адрес</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Телефон</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Действия</th>
            </tr>
          </thead>
          <tbody>
            {contractors.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-gray-400">
                  {search ? 'Контрагенты не найдены' : 'Нет контрагентов. Нажмите «Добавить».'}
                </td>
              </tr>
            ) : contractors.map((c) => (
              <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{c.unp}</td>
                <td className="px-4 py-3 text-gray-600 truncate max-w-[200px]">{c.address || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{c.phone || '—'}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      title="Редактировать"
                      onClick={() => handleEdit(c)}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      title="Удалить"
                      onClick={() => handleDelete(c.id)}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 mt-3">
        Всего: {contractors.length} контрагент(ов)
      </p>

      <ContractorModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditItem(null) }}
        editItem={editItem}
      />
    </div>
  )
}
